import { useState } from "react";
import DebouncedAutoComplete from "../../components/DebouncedAutoComplete";
import { ContactOption } from "./ContactOption";
import axios from "axios";
import { Container, Dialog } from "@mui/material";
import OrderEntryCreateContact from "./OrderEntryCreateContact";
import { Contact, GetDefaultContact } from "../../orderEntry/types/Contact";
import OrderEntryCreateCompany from "./OrderEntryCreateCompany";
import UserNewEditForm from "./new-contact";

export default function PhonebookContactAutocomplete({
  contact,
  setContact,
  apiOptions,
  contactTypes,
  textboxLabel,
  textboxPlaceholder,
  required,
  error,
  freeSolo,
  onInputChange,
  isCompany,
  disabled,
  isDisabledOptionForAdding,
  isLimitToContactsOnly,
  isLimitToCompaniesOnly,
  showAddOption = true, 
  apiUrl = "/api/clientphonebook/contacts/getOrderEntryContacts"
}: {
  contact: Contact | null;
  setContact: (value: Contact | null) => void;
  apiOptions?: object;
  contactTypes?: string[];
  textboxLabel: string;
  textboxPlaceholder?: string;
  required?: boolean;
  error?: boolean;
  freeSolo?: boolean;
  onInputChange?: (value: string | null) => void;
  isCompany?: boolean;
  disabled?: boolean;
  isDisabledOptionForAdding?: boolean;
  isLimitToContactsOnly?: boolean;
  isLimitToCompaniesOnly?: boolean;
  showAddOption?: boolean;
  apiUrl?: string;
}) {
  const [contactList, setContactList] = useState<Contact[]>([]);
  const [createNewContactModal, setCreateNewContactModal] = useState(false);
  const [inputText, setInputText] = useState("");
  const addOption = [{ ...GetDefaultContact(), isButton: true }];

  const getOrderEntryContacts = async (text: string, signal?: AbortSignal) => {
    const types = contactTypes?.length ? contactTypes : [undefined];
    const responses = await Promise.all(
      types.map((contactType) =>
        axios<Contact[]>({
          method: "get",
          url: apiUrl,
          params: {
            searchText: text || "",
            ...apiOptions,
            ...(contactType ? { contactType } : {})
          },
          signal
        })
      )
    );
    const seen = new Set<string>();
    let data = responses.flatMap((response) => response.data || []).filter((contactOption) => {
      const key = String(
        contactOption.lookupCode ||
          contactOption.id ||
          `${contactOption.name || ""}|${contactOption.company || ""}|${contactOption.email || ""}`
      ).toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (isLimitToCompaniesOnly) {
      data = data.filter((x) => !x.isIndividual);
    }

    if (isLimitToContactsOnly) {
      data = data.filter((x) => x.isIndividual);
    }

    const options = isDisabledOptionForAdding || !showAddOption ? data : addOption.concat(data as any);
    setContactList(options);
  };

  return (
    <>
      <DebouncedAutoComplete
        fullWidth
        freeSolo={freeSolo}
        popperWidth={650}
        options={contactList}
        value={contact}
        getOptionsLabel={(option) => (option.isIndividual ? option.name : option.company) || ""}
        onSelectedOptionChanged={(e, value) => {
          if (typeof value === "object") {
            setContact(value);
          }
        }}
        textboxLabel={textboxLabel}
        textboxPlaceholder={textboxPlaceholder || ""}
        onDebouncedTextChanged={getOrderEntryContacts}
        setText={setInputText}
        error={error}
        required={required}
        disabled={disabled}
        renderOption={(props, option) => {
          return option.isButton && showAddOption ? (
            <li {...props} key={option.name}>
              {isCompany && (
                <OrderEntryCreateCompany
                  inputText={inputText}
                  onNoOption={(b) => setCreateNewContactModal(true)}
                  onSave={(value: any) => {
                    setContact(value);
                  }}
                />
              )}
              {!isCompany && (
                <OrderEntryCreateContact
                  inputText={inputText}
                  onNoOption={(b) => setCreateNewContactModal(true)}
                  onSave={setContact}
                />
              )}
            </li>
          ) : (
            <ContactOption option={option} props={props} />
          );
        }}
        additionalOnChange={onInputChange}
      />
      <Dialog
        open={createNewContactModal}
        onClose={(event, reason) => {
          if (reason === "escapeKeyDown") {
            setCreateNewContactModal(false);
          }
        }}
        sx={{
          ".MuiDialog-paper": { maxWidth: 1000, width: "100%" },
          ".MuiContainer-root": { maxHeight: "90vh" }
        }}
      >
        <Container sx={{ width: "100%" }}>
          <UserNewEditForm
            afterSaveFunction={(client: any) => {
              if (client) {
                setContact(client);
              }
              setCreateNewContactModal(false);
            }}
            duplicateButtonText="Add to Order"
            submitButtonText="Save & Add to Order"
            onClose={() => setCreateNewContactModal(false)}
            name={inputText}
          />
        </Container>
      </Dialog>
    </>
  );
}
